import React, { useEffect, useRef, useContext, useMemo, useState, useCallback } from "react";
import { ColorContext } from "../../Context/ColorContext";
import ColorMenu from "../../ColorMenu";
import { X } from "lucide-react";
import { Divider, Spinner, Button, Switch } from "@heroui/react";

/**
 * A minimal sibling to DrawArtworkModal, specialized for a square avatar editor.
 * - Edits an N x N pixel grid (N = profileSize).
 * - Supports zoom, pan (right-mouse drag), and faint guides.
 * - Optional autosave (localStorage) while open.
 *
 * Props:
 *   isOpen: boolean (optional; if you prefer keep it always mounted, ignore)
 *   onCloseModal: () => void
 *   onSave: (base64Png: string) => void
 *   profileSize: number   // e.g. 7 → a 7x7 avatar
 *   initialBase64?: string  // base64 PNG (no data: prefix) to seed the editor
 *   storageKey?: string     // optional custom autosave key; default uses 'artparcel:avatar'
 *   forwardedRef?: React.Ref<HTMLDivElement>
 */
const DrawProfileAvatarModal = ({
  isOpen = true,
  onCloseModal,
  onSave,
  profileSize = 7,
  initialBase64,
  storageKey,
  forwardedRef,
}) => {
  const colorCtx = useContext(ColorContext);
  const selectedColor = colorCtx?.selectedColor ?? "#000000";

  const canvasRef = useRef(null);
  const worldCanvasRef = useRef(null);
  const worldCtxRef = useRef(null);
  const baselineRef = useRef(null);

  const [showGuides, setShowGuides] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  // layout + view
  const layoutRef = useRef({ scale: 1, dx: 0, dy: 0, cssW: 512, cssH: 512 });
  const viewRef = useRef({
    zoom: 1,
    minZoom: 0.2,
    maxZoom: 16,
    panX: 0,
    panY: 0,
    dragging: false,
    lastX: 0,
    lastY: 0,
  });

  const AUTOSAVE_KEY = useMemo(
    () => storageKey || `artparcel:avatar:${profileSize}`,
    [storageKey, profileSize]
  );

  // --- helpers
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const clearCanvas = (ctx, w, h) => ctx.clearRect(0, 0, w, h);

  const resizeVisibleCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { cssW: 512, cssH: 512 };

    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const CHROME_RESERVE = 160;
    const maxByWidth = Math.floor(vw * 0.8);
    const maxByHeight = Math.floor(vh * 0.92 - CHROME_RESERVE);
    const side = Math.max(160, Math.min(maxByWidth, maxByHeight));

    const cssW = side;
    const cssH = side;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    return { cssW, cssH };
  };

  const computeLayout = (bboxW, bboxH, cssW, cssH) => {
    const margin = 8;
    const usableW = Math.max(1, cssW - margin * 2);
    const usableH = Math.max(1, cssH - margin * 2);
    const scale = Math.max(1, Math.floor(Math.min(usableW / bboxW, usableH / bboxH)));
    const drawW = bboxW * scale;
    const drawH = bboxH * scale;
    const dx = Math.floor((cssW - drawW) / 2);
    const dy = Math.floor((cssH - drawH) / 2);
    return { scale, dx, dy, cssW, cssH };
  };

  // --- guides
  const drawGuides = (ctx, finalScale, drawX, drawY) => {
    const N = profileSize;
    // GRID
    ctx.save();
    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.imageSmoothingEnabled = false;

    for (let i = 0; i <= N; i++) {
      const x = Math.round(drawX + i * finalScale) + 0.5;
      ctx.moveTo(x, Math.round(drawY) + 0.5);
      ctx.lineTo(x, Math.round(drawY + N * finalScale) + 0.5);
    }
    for (let j = 0; j <= N; j++) {
      const y = Math.round(drawY + j * finalScale) + 0.5;
      ctx.moveTo(Math.round(drawX) + 0.5, y);
      ctx.lineTo(Math.round(drawX + N * finalScale) + 0.5, y);
    }
    ctx.stroke();
    ctx.restore();

    // OUTLINE
    ctx.save();
    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255,255,255,0.9)";

    const x0 = Math.round(drawX) + 0.5;
    const y0 = Math.round(drawY) + 0.5;
    const x1 = Math.round(drawX + N * finalScale) + 0.5;
    const y1 = Math.round(drawY + N * finalScale) + 0.5;

    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y0);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x0, y1);
    ctx.lineTo(x0, y0);
    ctx.stroke();
    ctx.restore();
  };

  async function urlToDataUrl(url) {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // "data:image/...;base64,...."
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const drawFrame = () => {
    const canvas = canvasRef.current;
    const world = worldCanvasRef.current;
    if (!canvas || !world) return;

    const ctx = canvas.getContext("2d");
    const { cssW, cssH, dx, dy, scale: baseScale } = layoutRef.current;
    const { zoom, panX, panY } = viewRef.current;

    clearCanvas(ctx, cssW, cssH);

    const finalScale = baseScale * zoom;
    const drawX = dx + panX;
    const drawY = dy + panY;

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(world, 0, 0, profileSize, profileSize, drawX, drawY, profileSize * finalScale, profileSize * finalScale);
    if (showGuides) drawGuides(ctx, finalScale, drawX, drawY);
    drawCirclePreviewMask(ctx, finalScale, drawX, drawY);
  };

  const drawCirclePreviewMask = (ctx) => {
    const { cssW, cssH } = layoutRef.current;

    // Circle fully touching all canvas edges
    const cx = cssW / 2;
    const cy = cssH / 2;
    const r = Math.min(cssW, cssH) / 2; // radius that touches all borders

    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.beginPath();
    ctx.rect(0, 0, cssW, cssH); // full canvas
    ctx.moveTo(cx + r, cy);
    ctx.arc(cx, cy, r, 0, Math.PI * 2, false);
    ctx.fill("evenodd"); // darken everything outside the circle
    ctx.restore();
  };

  // --- load/init
  useEffect(() => {
    if (!isOpen) return;

    // 1) build the world canvas (exact N x N)
    const world = document.createElement("canvas");
    world.width = profileSize;
    world.height = profileSize;
    const wctx = world.getContext("2d", { willReadFrequently: true });
    wctx.clearRect(0, 0, profileSize, profileSize);
    worldCanvasRef.current = world;
    worldCtxRef.current = wctx;

    // if there's an initial image, draw it (source-atop not needed; full square)
    const seed = async () => {
      if (!initialBase64) return;

        let src = initialBase64;
      // If the caller passed an S3 URL, DO NOT draw it directly.
      // Instead, require they pass a same-origin proxy URL or a data URL.
      if (/^https?:\/\//i.test(src) && !src.startsWith(window.location.origin)) {
        console.warn("Seed skipped: remote URL would taint canvas. Pass a data URL or same-origin proxy URL.");
        return;
      }
      if (!src.startsWith("data:")) {
        src = `data:image/png;base64,${src}`;
      }
      await new Promise((resolve) => {
        const img = new Image();
        // no crossOrigin needed for data URLs
        img.onload = () => {
          wctx.imageSmoothingEnabled = false;
          wctx.clearRect(0, 0, profileSize, profileSize);
          wctx.drawImage(img, 0, 0, profileSize, profileSize);
          resolve();
        };
        img.onerror = resolve;
        img.src = src;
      });
    };

    const init = async () => {
      await seed();

      // remember pristine
      try {
        baselineRef.current = world.toDataURL("image/png");
      } catch {}

      // 2) visible canvas sizing + layout
      const { cssW, cssH } = resizeVisibleCanvas();
      layoutRef.current = computeLayout(profileSize, profileSize, cssW, cssH);

      // 3) try autosave
      try {
        const raw = localStorage.getItem(AUTOSAVE_KEY);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved?.png && saved?.N === profileSize) {
            await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                wctx.clearRect(0, 0, profileSize, profileSize);
                wctx.drawImage(img, 0, 0);
                resolve();
              };
              img.onerror = resolve;
              img.src = saved.png;
            });
            if (saved.view) {
              viewRef.current.zoom = saved.view.zoom ?? 1;
              viewRef.current.panX = saved.view.panX ?? 0;
              viewRef.current.panY = saved.view.panY ?? 0;
            }
            setShowGuides(saved.view?.showGuides ?? true);
            setIsDirty(true);
          }
        }
      } catch {}

      viewRef.current.zoom = 1;
      viewRef.current.panX = 0;
      viewRef.current.panY = 0;

      drawFrame();
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profileSize, initialBase64, AUTOSAVE_KEY]);

  // --- window resize
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      const { cssW, cssH } = resizeVisibleCanvas();
      layoutRef.current = computeLayout(profileSize, profileSize, cssW, cssH);
      drawFrame();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profileSize]);

  // --- paint
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const wctx = worldCtxRef.current;
    if (!canvas || !wctx) return;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const { dx, dy, scale: baseScale } = layoutRef.current;
    const { zoom, panX, panY } = viewRef.current;

    const finalScale = baseScale * zoom;
    const drawX = dx + panX;
    const drawY = dy + panY;

    const lx = cssX - drawX;
    const ly = cssY - drawY;
    if (lx < 0 || ly < 0) return;

    const i = Math.floor(lx / finalScale);
    const j = Math.floor(ly / finalScale);
    if (i < 0 || j < 0 || i >= profileSize || j >= profileSize) return;

    wctx.imageSmoothingEnabled = false;
    wctx.fillStyle = selectedColor;
    wctx.fillRect(i, j, 1, 1);
    setIsDirty(true);
    queueAutosave();
    drawFrame();
  };

  // --- zoom/pan
  const handleWheel = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const { dx, dy, scale: baseScale } = layoutRef.current;
    const v = viewRef.current;

    const prevScale = baseScale * v.zoom;
    const worldX = (cssX - (dx + v.panX)) / prevScale;
    const worldY = (cssY - (dy + v.panY)) / prevScale;

    const factor = e.deltaY < 0 ? 1.2 : 0.8;
    const nextZoom = clamp(v.zoom * factor, v.minZoom, v.maxZoom);
    const newScale = baseScale * nextZoom;

    v.panX = cssX - dx - worldX * newScale;
    v.panY = cssY - dy - worldY * newScale;
    v.zoom = nextZoom;

    drawFrame();
    queueAutosave(1000);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 2) return; // right mouse drag to pan
    const v = viewRef.current;
    v.dragging = true;
    v.lastX = e.clientX;
    v.lastY = e.clientY;
    if (canvasRef.current) canvasRef.current.style.cursor = "grabbing";
  };
  const handleMouseMove = (e) => {
    const v = viewRef.current;
    if (!v.dragging) return;
    const dx = e.clientX - v.lastX;
    const dy = e.clientY - v.lastY;
    v.lastX = e.clientX;
    v.lastY = e.clientY;
    v.panX += dx;
    v.panY += dy;
    drawFrame();
    queueAutosave(1000);
  };
  const endDrag = () => {
    const v = viewRef.current;
    v.dragging = false;
    if (canvasRef.current) canvasRef.current.style.cursor = "crosshair";
  };

  const zoomIn = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const r = canvas.getBoundingClientRect();
    handleWheel({ preventDefault() {}, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, deltaY: -1 });
  };
  const zoomOut = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const r = canvas.getBoundingClientRect();
    handleWheel({ preventDefault() {}, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, deltaY: +1 });
  };
  const resetView = () => {
    const v = viewRef.current;
    v.zoom = 1; v.panX = 0; v.panY = 0;
    drawFrame();
  };

  // --- autosave
  const autosaveTimerRef = useRef(null);
  const queueAutosave = (delayMs = 600) => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(doAutosave, delayMs);
  };
  const doAutosave = () => {
    const world = worldCanvasRef.current;
    if (!world) return;
    try {
      const payload = {
        ts: Date.now(),
        N: profileSize,
        view: { zoom: viewRef.current.zoom, panX: viewRef.current.panX, panY: viewRef.current.panY, showGuides },
        png: world.toDataURL("image/png"),
      };
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload));
    } catch {}
  };
  const hasAutosave = useCallback(() => {
    try { return !!localStorage.getItem(AUTOSAVE_KEY); } catch { return false; }
  }, [AUTOSAVE_KEY]);
  const clearAutosave = useCallback(() => {
    try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
  }, [AUTOSAVE_KEY]);

  const restoreBaseline = useCallback(async () => {
    const world = worldCanvasRef.current;
    const wctx = worldCtxRef.current;
    if (!world || !wctx) return;
    const dataURL = baselineRef.current;
    wctx.clearRect(0, 0, profileSize, profileSize);
    if (dataURL) {
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => { wctx.drawImage(img, 0, 0); resolve(); };
        img.onerror = resolve;
        img.src = dataURL;
      });
    }
  }, [profileSize]);

  // --- save/discard/close
  const onSaveAvatar = async () => {
    const world = worldCanvasRef.current;
    if (!world) return;
    setIsBusy(true);
    try {
      const dataUrl = world.toDataURL("image/png");
      const base64 = dataUrl.split(",")[1];
      setIsBusy(false);
      setIsDirty(false);
      clearAutosave();
      try { baselineRef.current = world.toDataURL("image/png"); } catch {}
      onSave?.(base64);
    } catch (err) {
      console.error("Saving avatar failed:", err);
      setIsBusy(false);
    }
  };

  const onDiscard = async () => {
    const should = window.confirm("Discard unsaved avatar changes?");
    if (!should) return;
    clearAutosave();
    await restoreBaseline();
    setIsDirty(false);
    drawFrame();
  };

  const onCloseRequest = async () => {
    if (isDirty || hasAutosave()) {
      const ok = window.confirm("Discard unsaved changes and close?");
      if (!ok) return;
      clearAutosave();
      setIsDirty(false);
      try {
        await restoreBaseline();
        drawFrame();
      } catch {}
    }
    onCloseModal?.();
  };

  if (!isOpen) return null;

  return (
    <>
      {isBusy && (
        <div className="fixed inset-0 z-[1110] bg-black/70 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 flex items-center gap-2">
            <Spinner color="primary" size="lg" />
            <span className="text-black">saving avatar…</span>
          </div>
        </div>
      )}

      <div
        ref={forwardedRef}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1050] pointer-events-none"
      >
        <div className="pointer-events-auto bg-slate-900/80 rounded-xl p-4 relative w-fit max-w-[96vw] max-h-[92svh] overflow-hidden">
          <button onClick={onCloseRequest} className="absolute top-4 right-4 text-zinc-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>

          <h2 className="mb-2 text-center font-bold">Edit Profile Avatar ({profileSize}×{profileSize})</h2>

          <div className="overflow-hidden">
            <div className="relative">
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onContextMenu={(e) => e.preventDefault()}
                style={{ imageRendering: "pixelated", border: "1px solid black", cursor: "crosshair" }}
              />
            </div>
          </div>

          <Divider className="my-4" />

          <div className="flex mt-4 gap-2">
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                className="py-2 border-2 border-zinc-200 text-white rounded-2xl bg-slate-800 hover:bg-indigo-700"
                onPress={zoomOut}
              >
                −
              </Button>
              <Button
                size="sm"
                className="py-2 border-2 border-zinc-200 text-white rounded-2xl bg-slate-800 hover:bg-indigo-700"
                onPress={resetView}
              >
                Reset
              </Button>
              <Button
                size="sm"
                className="py-2 border-2 border-zinc-200 text-white rounded-2xl bg-slate-800 hover:bg-indigo-700"
                onPress={zoomIn}
              >
                +
              </Button>
              <Switch size="sm" isSelected={showGuides} onValueChange={(v) => { setShowGuides(v); drawFrame(); }}>
                Guides
              </Switch>
            </div>

            <Button
              className="py-2 border-2 border-zinc-200 text-white rounded-2xl bg-slate-800 hover:bg-indigo-700"
              variant="flat"
              onPress={onDiscard}
              disabled={!isDirty && !hasAutosave()}
            >
              Discard
            </Button>

            <Button
              onPress={onSaveAvatar}
              className="w-full py-2 border-2 border-zinc-200 text-white rounded-2xl bg-slate-800 hover:bg-indigo-700"
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* same color picker you already use */}
      <ColorMenu />
    </>
  );
};

export default DrawProfileAvatarModal;
